import DOMPurify from 'dompurify';

import { configSettings } from './config';
import * as Sentry from '@sentry/react';
import { queryClient } from '@/queries';
import { QUERY_KEYS } from './constants';
import { JWTData, LogLevel, SentryLogOptions, User } from '@/types';
import Cookies from 'js-cookie';
import { Page, PageStatus, PageType, Product, Store } from '@/types/store';
import { parseISO } from 'date-fns';
import { ServerPageData } from '@/features/product-editor/types';

const { logger } = Sentry;

/**
 * Downloads HTML content as a PDF file
 * Opens HTML in an iframe and triggers browser print dialog
 * @param htmlContent - The HTML content to convert to PDF
 * @param fileName - The name of the PDF file to download (used for print dialog title)
 */
export function downloadHtmlAsPDF(htmlContent: string, fileName: string): void {
    // Create an iframe to display the HTML content
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.zIndex = '9999';
    iframe.style.backgroundColor = 'white';

    iframe.onload = () => {
        const iframeWindow = iframe.contentWindow;
        const iframeDocument = iframe.contentDocument || iframeWindow?.document;

        if (!iframeWindow || !iframeDocument) {
            console.error('Unable to access iframe document');
            document.body.removeChild(iframe);
            return;
        }

        // Wait for all images and resources to load before printing
        const images = iframeDocument.querySelectorAll('img');
        const imagePromises: Promise<void>[] = [];

        images.forEach(img => {
            if (!img.complete) {
                imagePromises.push(
                    new Promise<void>(resolve => {
                        img.onload = () => resolve();
                        img.onerror = () => resolve(); // Resolve even on error to not block
                        // Timeout after 10 seconds as a fallback
                        setTimeout(() => resolve(), 10000);
                    })
                );
            }
        });

        // Wait for all images to load, then trigger print
        Promise.all(imagePromises)
            .then(() => {
                try {
                    // Temporarily update parent window title so browser uses it as filename
                    const originalTitle = document.title;
                    document.title = fileName;

                    // Trigger print dialog
                    iframeWindow.print();

                    // Restore original title after a short delay
                    setTimeout(() => {
                        document.title = originalTitle;
                        // Remove iframe after print dialog opens
                        if (document.body.contains(iframe)) {
                            document.body.removeChild(iframe);
                        }
                    }, 1000);
                } catch (error) {
                    console.error('Error triggering print:', error);
                    // Restore title on error
                    const originalTitle = document.title;
                    if (originalTitle !== fileName) {
                        // Only restore if we actually changed it
                        document.title = originalTitle;
                    }
                    document.body.removeChild(iframe);
                }
            })
            .catch(error => {
                console.error('Error waiting for images to load:', error);
                // Still try to print even if image loading failed
                try {
                    // Temporarily update parent window title so browser uses it as filename
                    const originalTitle = document.title;
                    document.title = fileName;

                    iframeWindow.print();

                    // Restore original title after a short delay
                    setTimeout(() => {
                        document.title = originalTitle;
                        if (document.body.contains(iframe)) {
                            document.body.removeChild(iframe);
                        }
                    }, 1000);
                } catch (printError) {
                    console.error('Error triggering print:', printError);
                    // Restore title on error
                    const originalTitle = document.title;
                    if (originalTitle !== fileName) {
                        document.title = originalTitle;
                    }
                    document.body.removeChild(iframe);
                }
            });
    };

    // Set the HTML content in the iframe
    iframe.srcdoc = htmlContent;
    document.body.appendChild(iframe);
}

/**
 * Downloads data as a CSV file
 * @param options - Configuration object containing filename, headers, and rows
 * @example
 * downloadCSV({
 *   filename: 'orders-2025-01-01',
 *   headers: ['Name', 'Email', 'Amount'],
 *   rows: [['John Doe', 'john@example.com', 100]]
 * });
 */
export function downloadCSV({
    filename,
    headers,
    rows,
}: {
    filename: string;
    headers: string[];
    rows: (string | number | boolean | null | undefined)[][];
}) {
    // Escape and format CSV cells
    const formatCell = (cell: string | number | boolean | null | undefined): string => {
        if (cell === null || cell === undefined) {
            return '';
        }
        const stringValue = String(cell);
        // Escape double quotes by doubling them
        const escaped = stringValue.replace(/"/g, '""');
        // Always wrap in quotes for safety
        return `"${escaped}"`;
    };

    // Combine headers and rows
    const csvContent = [headers.map(formatCell).join(','), ...rows.map(row => row.map(formatCell).join(','))].join(
        '\n'
    );

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up
}
export const norm = (s: string) => s.trim().toLowerCase();

// tags is 'any' so that we can force 'feature' tag if tags are used, but still have a default.
// We want to force 'feature' for future use (nice dashboards).
export function logWithSentry(
    message: string,
    { tags = {} as any, context = {}, level = 'error' }: SentryLogOptions = {}
) {
    Sentry.withScope(scope => {
        // Always add user_id
        tags['user_id'] = queryClient.getQueryData<User>([QUERY_KEYS.USER])?.user_id;

        Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
        });
        scope.setContext('Details', context);
        switch (level) {
            case 'info':
                Sentry.captureMessage(message, 'info');
                break;
            case 'warning':
                Sentry.captureMessage(message, 'warning');
                break;
            case 'error':
                Sentry.captureMessage(message, 'error');
                break;
            default:
                Sentry.captureMessage(message, 'error');
                break;
        }
    });

    const logLevelToConsoleMethod: Record<LogLevel, keyof Console> = {
        info: 'info',
        warning: 'warn', // 'warning' must map to 'warn'
        error: 'error',
        log: 'log',
    };
    // all this does is console log on local env
    if (configSettings.environment !== 'staging' && configSettings.environment !== 'production') {
        const method = logLevelToConsoleMethod[level];

        // Any is used here because there isn't an easy way type this so that consoleFn works lol
        // eslint-disable-next-line no-console
        const consoleFn = console[method] as (...args: any[]) => void;

        if (typeof consoleFn === 'function') {
            consoleFn(`[${logger}] ${message}`, context);
        }
    }
}

export function copyToClipboard(
    textToCopy: string,
    options?: {
        successMessage?: string;
        errorMessage?: string;
        showNotifications?: boolean;
    }
): Promise<void> {
    const {
        successMessage = 'Copied to clipboard!',
        errorMessage = 'Failed to copy to clipboard',
        showNotifications = true,
    } = options || {};

    return new Promise((resolve, reject) => {
        // Try modern clipboard API first
        navigator.clipboard
            .writeText(textToCopy)
            .then(async () => {
                if (showNotifications) {
                    const { stanNotify } = await import('@/lib/stanNotify');
                    stanNotify({
                        type: 'success',
                        title: successMessage,
                        duration: 2000,
                    });
                }
                resolve();
            })
            .catch(async err => {
                console.error('Failed to copy text: ', err);

                // Fallback to execCommand
                const textarea = document.createElement('textarea');
                textarea.value = textToCopy;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();

                let success = false;
                try {
                    success = document.execCommand('copy');
                } catch (fallbackErr) {
                    console.error('Failed to copy text using execCommand: ', fallbackErr);
                }

                document.body.removeChild(textarea);

                if (success) {
                    if (showNotifications) {
                        const { stanNotify } = await import('@/lib/stanNotify');
                        stanNotify({
                            type: 'success',
                            title: successMessage,
                            duration: 2000,
                        });
                    }
                    resolve();
                } else {
                    if (showNotifications) {
                        const { stanNotify } = await import('@/lib/stanNotify');
                        stanNotify({
                            type: 'error',
                            title: errorMessage,
                            duration: 2000,
                        });
                    }
                    reject(new Error('Failed to copy text to clipboard'));
                }
            });
    });
}

export function getUUID(): string {
    const lut: string[] = Array(256);
    for (let i = 0; i < 256; i++) {
        lut[i] = (i < 16 ? '0' : '') + i.toString(16);
    }

    const getHex = (num: number): string => {
        const hex = lut[num & 0xff];
        if (!hex) throw new Error(`Invalid index in LUT: ${num & 0xff}`);
        return hex;
    };

    const d0 = (Math.random() * 0xffffffff) | 0;
    const d1 = (Math.random() * 0xffffffff) | 0;
    const d2 = (Math.random() * 0xffffffff) | 0;
    const d3 = (Math.random() * 0xffffffff) | 0;

    return (
        getHex(d0) +
        getHex(d0 >> 8) +
        getHex(d0 >> 16) +
        getHex(d0 >> 24) +
        '-' +
        getHex(d1) +
        getHex(d1 >> 8) +
        '-' +
        getHex(((d1 >> 16) & 0x0f) | 0x40) +
        getHex(d1 >> 24) +
        '-' +
        getHex((d2 & 0x3f) | 0x80) +
        getHex(d2 >> 8) +
        '-' +
        getHex(d2 >> 16) +
        getHex(d2 >> 24) +
        getHex(d3) +
        getHex(d3 >> 8) +
        getHex(d3 >> 16) +
        getHex(d3 >> 24)
    );
}

export function getSessionUUID() {
    // We save session for 10 minutes and renew every time there's an event to be tracked
    const sessionUuid = Cookies.get('session_uuid') || getUUID();
    const in10Minutes = 1 / 24 / 6; // js-cookies uses days as base unit
    Cookies.set('session_uuid', sessionUuid, { expires: in10Minutes });
    Cookies.set('session_uuid', sessionUuid, { domain: 'stanwith.me', expires: in10Minutes });
    Cookies.set('session_uuid', sessionUuid, { domain: 'stan.store', expires: in10Minutes });
    return sessionUuid;
}

export function removePrefixIfExists(strToCheck: string, prefix: string) {
    if (strToCheck.startsWith(prefix)) {
        return strToCheck.slice(prefix.length); // Remove the prefix
    }
    return strToCheck; // Return the original string if no prefix is found
}
export function validateMFACodeFormat(code: string) {
    const re = /^\d{6}$/;
    return re.test(code);
}

export function isValidURL(url: string) {
    const urlRegex =
        /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
    return urlRegex.test(url);
}

export function isValidURLWithoutHttp(url: string) {
    const httpLessUrlRegex =
        /^(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
    return httpLessUrlRegex.test(url);
}

export function autoAddHttps(url: string) {
    if (isValidURLWithoutHttp(url) && !isValidURL(url)) {
        url = `https://${url}`;
    }
    if (url) {
        url = url.replaceAll(' ', '');
    }
    return url;
}

// Attempts to convert numeric timestamps to Date instances, returning null on failure.
export function parseDateFromNumber(value: number) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

// Attempts to convert ISO-like strings to Date instances with a native fallback on failure.
export function parseDateFromString(value: string) {
    try {
        const parsed = parseISO(value);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    } catch {
        /* noop */
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Remove https:// or http:// from URL for display
 */
export function removeHttps(url: string): string {
    return url.replace(/^https?:\/\//, '');
}

/**
 * Formats link display text by removing protocol and www
 */
export function formatLinkDisplayText(url: string): string {
    return String(url)
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '');
}

/**
 * Format a number with optional compact notation and decimal places
 * Matches Vue implementation from global_helper/helpers.js
 */
export function formatNumber(
    number: number,
    { locale = 'en-US', compact = false, decimals = 0 }: { locale?: string; compact?: boolean; decimals?: number } = {}
): string {
    return Intl.NumberFormat(
        locale,
        compact
            ? { notation: 'compact', compactDisplay: 'short' }
            : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
    ).format(Math.abs(number));
}

/**
 * Check if a page has membership pricing
 */
export function isMembershipPricing(page: Page): boolean {
    const isMembershipProduct = page?.type === 'membership';
    const hasMembershipInterval = page?.data?.product?.price?.interval;
    return isMembershipProduct || !!hasMembershipInterval;
}

/**
 * Check if a product is paid (has a price > 0)
 */
// export function isPaidProduct(product: Product) {
// TODO FIX
export function isPaidProduct(product: ServerPageData['product']) {
    let price = 0;
    if (product.price) price = product.price.sale_amount ? product.price.sale_amount : product.price.amount;
    return price > 0;
}

/**
 * Check if a page represents a paid product
 */
export function isPaidPage(page: Page): boolean {
    return isPaidProduct(page.data.product);
}

/**
 * Strip all HTML tags from a string
 * Useful for converting rich text to plain text
 *
 * @param html - The HTML string to strip
 * @returns Plain text without any HTML tags
 */
export function stripHtmlTags(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
}

/**
 * Compare two HTML strings by their stripped content
 * Useful for comparing rich text content regardless of HTML wrapper differences
 *
 * @param html1 - First HTML string to compare
 * @param html2 - Second HTML string to compare
 * @returns true if the stripped content is identical, false otherwise
 */
export function areHtmlContentsEqual(html1: string, html2: string): boolean {
    return stripHtmlTags(html1) === stripHtmlTags(html2);
}

/**
 * Format minutes since midnight to 12-hour format with AM/PM for API
 * Matches the Vue implementation's getTimesAPIValue function
 *
 * @param totalMinutes - Total minutes since midnight (0-1439)
 * @returns Time string in format "HH:MM AM/PM"
 *
 * @example
 * formatTimeForAPI(0) // Returns "12:00 AM"
 * formatTimeForAPI(840) // Returns "02:00 PM" (14 * 60 = 840)
 * formatTimeForAPI(870) // Returns "02:30 PM" (14 * 60 + 30 = 870)
 */
export function formatTimeForAPI(totalMinutes: number): string {
    const hour = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Handle midnight and noon special cases
    let displayHour: number;
    let period: string;

    if (hour === 0 || hour === 24) {
        displayHour = 12;
        period = 'AM';
    } else if (hour === 12) {
        displayHour = 12;
        period = 'PM';
    } else if (hour < 12) {
        displayHour = hour;
        period = 'AM';
    } else {
        displayHour = hour - 12;
        period = 'PM';
    }

    // Format with leading zeros
    return `${displayHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Parse time from API format (12-hour with AM/PM) to total minutes since midnight
 * Reverse of formatTimeForAPI function
 *
 * @param timeString - Time string in format "HH:MM AM/PM" (e.g., "02:00 PM", "02:30 PM")
 * @returns Total minutes since midnight (0-1439)
 *
 * @example
 * parseTimeFromAPI("02:00 PM") // Returns 840 (14 * 60)
 * parseTimeFromAPI("02:30 PM") // Returns 870 (14 * 60 + 30)
 * parseTimeFromAPI("12:00 AM") // Returns 0
 * parseTimeFromAPI("12:00 PM") // Returns 720 (12 * 60)
 */
export function parseTimeFromAPI(timeString: string): number {
    const [time, period] = timeString.split(' ');
    const [hourStr = '0', minuteStr = '0'] = time?.split(':') || [];
    const hour = parseInt(hourStr, 10);
    const minutes = parseInt(minuteStr, 10);

    let hour24: number;

    // Handle special cases
    if (period === 'AM') {
        // 12:XX AM is midnight hour (0)
        hour24 = hour === 12 ? 0 : hour;
    } else {
        // 12:XX PM stays as 12, other PM hours add 12
        hour24 = hour === 12 ? 12 : hour + 12;
    }

    return hour24 * 60 + minutes;
}

/**
 * Output format options for buildProducts
 */
export type BuildProductsFormat = 'products' | 'tags' | 'options' | 'labels';

/**
 * Filter options for product building
 */
export interface BuildProductsFilters {
    type?: PageType | null;
    status?: PageStatus;
    excludeLinks?: boolean;
    membershipOnly?: boolean;
    paidOnly?: boolean;
}

/**
 * Options for building product lists
 */
export interface BuildProductsOptions {
    store: Store;
    funnels?: Store[];
    sections?: { section_id?: number; data: { title?: string; image?: string } }[];
    filters?: BuildProductsFilters;
    format?: BuildProductsFormat;
    user?: User | null;
}

/**
 * Product option for tag selector (audience builder format)
 */
export interface ProductOption {
    tag: string;
    type: 'pseudo';
    data: {
        label: string;
        storeName: string;
        image: string;
        id?: number;
        type: string;
        tag: string;
        productId?: number;
        membershipId?: number;
        location: string;
        section_id?: number;
    };
}

/**
 * Product option for filter dropdowns
 */
export interface ProductFilterOption {
    label: string;
    id?: number;
    icon: string;
}

/**
 * Matches Vue implementation: /src/global_helper/helpers.js - buildProductList()
 *
 * @param options - Configuration object for building products
 * @param options.store - The main store object containing pages
 * @param options.funnels - Optional array of funnel stores to include
 * @param options.sections - Optional array of sections to include (for audience builder)
 * @param options.filters - Optional filtering criteria
 * @param options.filters.type - Filter by page type (e.g., 'course', 'download')
 * @param options.filters.status - Filter by page status (published, draft, etc.)
 * @param options.filters.excludeLinks - Whether to exclude link-type pages (default: true)
 * @param options.filters.membershipOnly - Only include membership products
 * @param options.filters.paidOnly - Only include paid products
 * @param options.format - Output format: 'products' (default), 'tags', 'options', 'labels'
 * @param options.user - User object for generating URLs
 *
 * @returns Array of products in the specified format
 *
 * @example
 * // For product selectors (original buildProductList behavior)
 * const products = buildProducts({
 *   store,
 *   funnels,
 *   filters: { excludeLinks: true, status: PageStatus.PUBLISHED, paidOnly: true },
 *   format: 'products',
 *   user
 * });
 *
 * @example
 * // For audience builder (original buildProductOptions behavior)
 * const tags = buildProducts({
 *   store,
 *   sections,
 *   format: 'tags'
 * });
 *
 * @example
 * // For filter dropdowns
 * const options = buildProducts({
 *   store,
 *   funnels,
 *   format: 'options'
 * });
 */
export function buildProducts(
    options: BuildProductsOptions
): Product[] | ProductOption[] | ProductFilterOption[] | string[] {
    const { store, funnels = [], sections = [], filters = {}, format = 'products', user = null } = options;

    const {
        type = null,
        status = PageStatus.DOES_NOT_EXIST,
        excludeLinks = true,
        membershipOnly = false,
        paidOnly = false,
    } = filters;

    // Create default store without name for consistency with Vue
    const defaultStoreNoName: Store = { ...store, data: { ...store.data, name: '' } };
    const defaultStoreId = store.store_id;

    // Combine default store with funnels
    const stores = [defaultStoreNoName, ...funnels];

    // Collect and filter product pages from all stores
    const productPages: Page[] = [];

    stores.forEach(s => {
        if (s) {
            let pages = s.pages || [];

            // Apply filters
            if (type) {
                pages = pages.filter(p => p.type === type);
            }
            if (excludeLinks) {
                pages = pages.filter(p => !['link', 'stan-referral-link'].includes(p.type));
            }
            if (status > PageStatus.DOES_NOT_EXIST) {
                pages = pages.filter(p => p.status === status);
            }
            if (membershipOnly) {
                pages = pages.filter(p => isMembershipPricing(p));
            }
            if (paidOnly) {
                pages = pages.filter(p => isPaidProduct(p.data.product));
            }

            // Add store name to pages for distinction
            const storeName = s?.data?.name;
            pages = pages.map(p => ({ ...p, storeName }));

            productPages.push(...pages);
        }
    });

    // Format output based on requested format
    switch (format) {
        case 'labels': {
            // Return distinct product titles (Vue's distinctLabels behavior)
            const titles = productPages.map(p => p.data.product.title?.trim()).filter(Boolean);
            return [...new Set(titles)];
        }

        case 'tags': {
            // Return ProductOption format for audience builder
            const tags: ProductOption[] = [];

            // Add products from pages
            productPages.forEach(page => {
                if (page.page_id && page.data?.product?.title) {
                    tags.push({
                        tag: `Purchased: ${page.data.product.title.trim()}`,
                        type: 'pseudo',
                        data: {
                            label: page.data.product.title.trim(),
                            storeName: page.storeName || '',
                            image:
                                (page.data.location === 'store' && page.store_id === defaultStoreId
                                    ? page.data.button?.image
                                    : page.data.product.image) || '',
                            id: page.page_id,
                            type: page.type,
                            tag: `Purchased: ${page.data.product.title.trim()}`,
                            productId: page.data.product.id,
                            membershipId:
                                page.type === 'membership'
                                    ? page.data.product.id
                                    : page.data.product?.price?.membership_id,
                            location: page.data.location || '',
                        },
                    });
                }
            });

            // Add sections if provided
            sections.forEach(section => {
                if (section.data?.title) {
                    tags.push({
                        tag: `Purchased: ${section.data.title}`,
                        type: 'pseudo',
                        data: {
                            label: section.data.title,
                            storeName: '',
                            image: section.data.image || '',
                            tag: `Purchased: ${section.data.title}`,
                            section_id: section.section_id,
                            type: 'section',
                            location: 'section',
                        },
                    });
                }
            });

            return tags;
        }

        case 'options': {
            // Return format suitable for filter dropdowns
            return productPages.map(p => ({
                label: p.storeName ? `${p.data.product.title.trim()} - ${p.storeName}` : p.data.product.title.trim(),
                id: p.page_id,
                icon:
                    (p.data.location === 'store' && p.store_id === defaultStoreId
                        ? p.data.button?.image
                        : p.data.product.image) || '',
            })) as ProductFilterOption[];
        }

        case 'products':
        default: {
            // Return Product format
            return productPages.map(p => ({
                title: p.data.product.title.trim(),
                storeName: p.storeName,
                image:
                    (p.data.location === 'store' && p.store_id === defaultStoreId
                        ? p.data.button?.image
                        : p.data.product.image) || '',
                id: p.page_id,
                type: p.type,
                tag: `Purchased: ${p.data.product.title.trim()}`,
                productId: p.data.product.id,
                membershipId: p.type === 'membership' ? p.data.product.id : p.data.product?.price?.membership_id,
                location: p.data.location,
                url: user ? `${configSettings.frontDomain}${user.username}/p/${p.slug}` : undefined,
            })) as Product[];
        }
    }
}

export function parseJWT(jwt: string): JWTData {
    const [header, payload, signature] = jwt.split('.');

    const parseData = (toParse: string) => {
        const base64 = toParse.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map(c => {
                    const charCode = `00${c.charCodeAt(0).toString(16)}`.slice(-2);
                    return `%${charCode}`;
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    };

    return {
        header: header ? parseData(header) : {},
        payload: payload ? parseData(payload) : {},
        signature,
    };
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if expired or invalid, false if still valid
 */
export function isTokenExpired(token: string): boolean {
    try {
        const parsed = parseJWT(token);

        if (!parsed?.payload?.exp) {
            return true;
        }

        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime >= parsed.payload.exp;
    } catch {
        // If token parsing fails (malformed token, invalid base64, etc.), treat as expired
        return true;
    }
}

export function getLocalTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

/**
 * Sanitizes HTML content to prevent XSS attacks using DOMPurify
 *
 * @param dirty - The HTML string to sanitize
 * @param options - Optional DOMPurify configuration
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * const safeHtml = sanitizeHtml('<script>alert("xss")</script><p>Hello</p>');
 * // Returns: '<p>Hello</p>'
 */
export function sanitizeHtml(
    dirty: string,
    options?: {
        ALLOWED_TAGS?: string[];
        ALLOWED_ATTR?: string[];
        KEEP_CONTENT?: boolean;
    }
): string {
    const defaultConfig = {
        ALLOWED_TAGS: [
            'p',
            'br',
            'hr',
            'strong',
            'em',
            'u',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'ul',
            'ol',
            'li',
            'a',
            'code',
            'pre',
            'blockquote',
            'span',
            'div',
            'img',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt'],
        KEEP_CONTENT: true, // Keep text content even when removing tags
        ALLOW_DATA_ATTR: false,
    };

    const config = options ? { ...defaultConfig, ...options } : defaultConfig;

    return DOMPurify.sanitize(dirty, config);
}

export const downloadFile = (url: string, name: string) => {
    if (!url || !name) {
        return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};
